import React from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Grid,
  Box,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Bar } from "react-chartjs-2";

// Function to format numbers in scientific notation
const formatToScientific = (value) => {
  // if (value === 0) return "0";
  // const exponent = Math.floor(Math.log10(Math.abs(value)));
  // const mantissa = (value / Math.pow(10, exponent)).toFixed(3);
  // return `$${mantissa} × 10^${exponent}`;

  if (value === 0) return value; // Return 0 as is

  const exponent = Math.floor(Math.log10(Math.abs(value)));
  const mantissa = (value / Math.pow(10, exponent)).toFixed(2); // Mantissa with 2 decimal places

  // If the exponent is between -2 and 2 (inclusive), return the rounded value with 2 decimal places
  if (exponent >= -2 && exponent <= 2) {
    return Number(value).toFixed(2);
  }

  // Otherwise, return the mantissa and exponent in scientific notation
  return (
    <>
      {mantissa} &times; 10<sup>{exponent}</sup>
    </>
  );
};

const formatToScientificForAxis = (value) => {
  if (value === 0) return value.toString(); // Return 0 as a string

  const exponent = Math.floor(Math.log10(Math.abs(value)));
  const mantissa = (value / Math.pow(10, exponent)).toFixed(2); // Mantissa with 2 decimal places

  // If the exponent is between -2 and 2 (inclusive), return the rounded value as a string
  if (exponent >= -2 && exponent <= 2) {
    return Number(value).toFixed(2).toString();
  }

  // Return the mantissa and exponent as a string in scientific notation
  return `${mantissa} × 10^${exponent}`;
};

// Function to generate bar chart data
const generateBarChartData = (bot) => {
  const keys = Object.keys(bot).filter((key) => key !== "botName");
  const data = keys.map((key) => bot[key] || 0);
  return {
    labels: keys.map((key) => key.replace(/([A-Z])/g, " $1").trim()),
    datasets: [
      {
        label: bot.botName,
        data: data,
        backgroundColor: "rgba(75, 192, 192, 0.7)",
        barThickness: 10, // Set the bar thickness
        maxBarThickness: 10, // Max bar thickness
      },
    ],
  };
};

const BotDetailsAccordion = ({ bots }) => {
  return (
    <div>
      {bots.map((bot, index) => (
        <Accordion
          key={index}
          sx={{
            backgroundColor: "#2a2a2a", // Dark background for accordion
            color: "#ffffff", // White text for contrast
            borderRadius: 2,
            marginBottom: 2,
            "&:before": {
              display: "none", // Remove default border styling
            },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ color: "#ffffff" }} />}
            sx={{
              backgroundColor: "#333333", // Slightly lighter background for summary
              "&:hover": {
                backgroundColor: "#444444", // Hover effect for summary
              },
              borderRadius: 1,
            }}
          >
            <Typography sx={{ fontWeight: "bold" }}>
              {bot.botName} {bot.botName === "Unknown" && "(Deleted)"}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ backgroundColor: "#2a2a2a" }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                {Object.keys(bot)
                  .filter((key) => key !== "botName")
                  .map((key, idx) => (
                    <Typography
                      key={idx}
                      sx={{ color: "#cccccc", marginBottom: 1 }}
                    >
                      {key.replace(/([A-Z])/g, " $1").trim()}:{" "}
                      {formatToScientific(bot[key] || 0)}
                    </Typography>
                  ))}
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ height: "200px", width: "100%" }}>
                  <Bar
                    data={generateBarChartData(bot)}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        x: {
                          ticks: { color: "#ffffff" },
                          grid: { color: "rgba(255, 255, 255, 0.1)" },
                        },
                        y: {
                          ticks: {
                            color: "#ffffff",
                            callback: (value) =>
                              formatToScientificForAxis(value), // Format Y-axis labels
                          },
                          grid: { color: "rgba(255, 255, 255, 0.1)" },
                        },
                      },
                      plugins: {
                        legend: {
                          labels: { color: "#ffffff" },
                        },
                      },
                    }}
                  />
                </Box>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      ))}
    </div>
  );
};

export default BotDetailsAccordion;
